import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';
import { appContext } from '../../../AppContext';
import { DateTime } from 'luxon';
import Helper from '../../../Common/Helper';
import apis from '../../../Providers.Api/apis';
import IbssDialog from '../BaseDialog/IbssDialog';
import LoadingOverlay from '../../Navigation/LoadingOverlay/LoadingOverlay';
import { ISelectOption } from '../../Inputs/SelectList/IbssInputDropDown';
import IbssButton from '../../Buttons/Button/IbssButton';
import { INote } from '../../Lists/Notes/Notes';
import { EventType } from '../../../Providers.Api/EventTypes';
import { PagedResponse } from '../../../Providers.Api/Models';
import { Space } from './DataModels';
import Guid from '../../../Common/Guid';
import { Visitor, IVisitorChange } from '../../Forms/Visitor/Visitor';
import { IUser } from '../../../Components/Inputs/UserPicker/UserPicker';
import { rights } from '../../../Common/RightsManager';

class CreateVisitorDialog extends IbssComponent<IProps, IState>
{
    private get visitsService() { return appContext().visitsService; }
    private get appState() { return appContext().state; }
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private get apiCache() { return appContext().apiCache; }

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            isValid: false,
            isLoading: false,
            firstName: '',
            lastName: '',
            company: '',
            email: '',
            visitorTypeId: '',
            invalidVisitorEmail: false,
            hostEmail: '',
            host: null,
            pacs: '',
            arrival: DateTime.now().plus({ minutes: 5 }).offsetTimeByNode(this.appState.buildingId),
            departure: DateTime.now().plus({ minutes: 10 }).offsetTimeByNode(this.appState.buildingId),
            buildingId: this.appState.buildingId,
            creatingVisitor: false,
            arrivalError: "",
            departureError: "",
            spaceName: "",
            spaceId: "",
            notes: [],
            arrivalSpaces: [],
            visitorArrivalLocation: "",
            isFlex: false,
            buildingOption: [],
        }
    }

    private get defaultFlexState(): IFlexInterface
    {
        return {
            isValid: false,
            isLoading: false,
            firstName: '',
            lastName: '',
            company: '',
            email: '',
            visitorTypeId: '',
            invalidVisitorEmail: false,
            hostEmail: '',
            host: null,
            pacs: '',
            arrival: DateTime.now().plus({ minutes: 5 }).offsetTimeByNode(this.appState.buildingId),
            departure: DateTime.now().plus({ minutes: 10 }).offsetTimeByNode(this.appState.buildingId),
            buildingId: this.appState.buildingId,
            creatingVisitor: false,
            arrivalError: "",
            departureError: "",
            spaceName: "",
            spaceId: "",
            notes: [],
            arrivalSpaces: [],
            visitorArrivalLocation: "",
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const buildings = Helper.getAllBuildingsData().sort((a, b) => a.Name.localeCompare(b.Name));
        const options = buildings.map(i => ({ value: i.Node_Id, label: i.Name }) as IBuildingOption);
        this.setState({ buildingOption: options });
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        if (prevProps.showCreateVisitorDialog !== this.props.showCreateVisitorDialog)
        {
            const isflex = window.location.href.includes('flex');
            this.appState.autoMap(this, i => ({ buildingId: i.buildingId }));
            if (isflex)
            {
                this.setState({ isFlex: true });
            }
            this.setState(this.defaultFlexState);
            if (this.props.showCreateVisitorDialog)
            {
                const currentUser = this.local.getUserDetails().email;
                if (currentUser && isflex)
                {
                    this.setState({
                        hostEmail: currentUser,
                    });
                }
                await this.getLocationDetails(this.state.buildingId);
                await this.getSpaceDetails(this.state.buildingId);
            }
        }

        if (prevState.buildingId !== this.state.buildingId)
        {
            this.setState({
                arrival: DateTime.now().plus({ minutes: 5 }).offsetTimeByNode(this.state.buildingId),
                departure: DateTime.now().plus({ minutes: 10 }).offsetTimeByNode(this.state.buildingId),
            })
        }
    }

    private async getLocationDetails(selectedBuildingId: number): Promise<void>
    {
        try 
        {
            const arrivalLocation: ISelectOption<string, string>[] = [];
            const spaces = await appContext().ibssApiClientV2.v2.byNodeid.spaces.get<PagedResponse<Space[]>>({
                nodeId: selectedBuildingId,
                filter: "Space_Arrival_Loc eq 1",
                select: Space,
            });
            spaces.value.forEach((item) =>
            {
                arrivalLocation.push({
                    label: item.Space_Name,
                    value: item.Space_Id,
                })
            })

            this.setState({ arrivalSpaces: arrivalLocation });
        }
        catch (error)
        {
        }
    }

    private async buildingChanged(buildingId: string): Promise<void>
    {
        this.setState({ 
            buildingId: parseInt(buildingId),
            spaceName:"" 
        });
        await this.getLocationDetails(parseInt(buildingId));
        await this.getSpaceDetails(parseInt(buildingId));
    }

    private async getSpaceDetails(selectedBuildingId: number): Promise<void>
    {
        try 
        {
            const visitorArrivalLocation = await apis.getParametersByName(selectedBuildingId);
            const spaceId = visitorArrivalLocation.data.Parameter_Value;
            if (spaceId)
            {
                const spaceList = await this.apiCache.getSpacesByBuilding(selectedBuildingId);
                const space = spaceList.find(x => x.Space_Id === spaceId);
                const spaceName = space ? space.Space_Name : '';
                await this.setState({
                    spaceName: spaceName,
                    spaceId: spaceId,
                    arrivalSpaces: [{ label: spaceName, value: spaceId }, ...this.state.arrivalSpaces.filter(space => space.value !== spaceId)],
                    visitorArrivalLocation: spaceId
                });
            }
        }
        catch (error)
        {
        }
    }

    private getVisitorEmail(visitorEmail: string): string
    {
        // if user inputs a visitor email, use that, else use the no-reply email with the randomly generated string.
        return visitorEmail || `no-reply-${Guid.new()}@noreply.com`;
    }

    private async createVisitorRecord(): Promise<void>
    {
        try
        {
            this.setState({ creatingVisitor: true });

            const payload = {
                Space_Id: this.state.spaceName && this.state.visitorArrivalLocation ? this.state.visitorArrivalLocation : "",
                Space_Name: this.state.spaceName ? this.state.spaceName : "",
                Visitor_First_Name: this.state.firstName,
                Visitor_Last_Name: this.state.lastName,
                Visitor_Email: this.getVisitorEmail(this.state.email),
                Visitor_Company: this.state.company,
                Visit_Host_Name: this.state.host?.displayName ?? '',
                Visit_Host_Email: this.state.host?.email ?? '',
                Visit_Start_Date: this.state.arrival.setZoneByNode(this.state.buildingId).toUTC().toISO(),
                Visit_End_Date: this.state.departure.setZoneByNode(this.state.buildingId).toUTC().toISO(),
                Visit_Approval_Comments: "",
                DisableExtUpdate: false,
                Visit_Pacs_Id: this.state.pacs,
                Booking_Id: "",
                Booking_Start: "",
                Booking_End: "",
                Visitor_Dietary_Pref: 0,
                Visit_Save_Info: 0,
                Disable_Ext_Update: false,
                VisitorTypeId: this.state.visitorTypeId==="None" ? null : this.state.visitorTypeId,
            };
            const visitResponse = await this.visitsService.create(this.state.buildingId, payload);

            for (const note of this.state.notes)
            {
                try
                {
                    await appContext().ibssApiClientV2.v2.byNodeid.notes.post({
                        nodeId: this.state.buildingId,
                        body: {
                            Node_Id: this.state.buildingId,
                            Event_Id: visitResponse.Visit_Id,
                            EventType_Id: EventType.visits.id,
                            EventType_Name: EventType.visits.name,
                            Note: note.value,
                            IsDeleted: 0,
                        },
                    });
                }
                catch { }
            }

            const buildingData = this.local.getNodeData().Regions.flatMap(i => i.Buildings).find(x => x.Node_Id == this.state.buildingId);
            const visitArrivalWindow = buildingData?.Vis_Arvl_Wndw_Mins;
            const visitReqsApproval = buildingData?.Vis_Req_Approval;

            if (visitReqsApproval == 1 && rights.API.Visits.Approve.has(this.state.buildingId) && visitResponse.Visit_IsApproved == 0)
            {
                try
                {
                    await this.visitsService.approve(this.state.buildingId, [visitResponse.Visit_Id], '');
                }
                catch { }
            }

            if (rights.API.Visits.CheckIn.has(this.state.buildingId) && !this.state.isFlex && this.state.arrival.minus({ minutes: visitArrivalWindow }) <= DateTime.now().offsetTimeByNode(this.state.buildingId))
            {
                try
                {
                    const body = [{
                        Visit_Id: visitResponse.Visit_Id,
                        VisitorTypeWorkflow: visitResponse.CheckInWorkflow ? JSON.parse(visitResponse.CheckInWorkflow) : { Steps: [] }
                    }];

                    await appContext().ibssApiClientV2.v2.byNodeid.visits.checkin.post({
                        nodeId: this.state.buildingId,
                        body: body,
                    });
                }
                catch { }
            }

            this.props.closeClicked(true);
        }
        finally
        {
            this.setState({ creatingVisitor: false });
        }
    }

    private handleVisitorChange(props: IVisitorChange): void
    {
        if (props.firstName != undefined) { this.setState({ firstName: props.firstName }); }
        if (props.lastName != undefined) { this.setState({ lastName: props.lastName }); }
        if (props.email != undefined) { this.setState({ email: props.email }); }
        if (props.visitorTypeId != undefined) { this.setState({ visitorTypeId: props.visitorTypeId }); }
        if (props.company != undefined) { this.setState({ company: props.company }); }
        if (props.pacs != undefined) { this.setState({ pacs: props.pacs }); }
        if (props.notes != undefined) { this.setState({ notes: props.notes }); }
        if (props.hostEmail != undefined) { this.setState({ hostEmail: props.hostEmail }); }
        if (props.host != undefined) { this.setState({ host: props.host }); }
        if (props.visitorStartDate != undefined) { this.setState({ arrival: props.visitorStartDate }); }
        if (props.visitorEndDate != undefined) { this.setState({ departure: props.visitorEndDate }); }

        if (props.arrivalLocationId != undefined)
        {
            const arrivalLocation = this.state.arrivalSpaces.find(i => i.value === props.arrivalLocationId);
            if (arrivalLocation)
            {
                this.setState({ visitorArrivalLocation: props.arrivalLocationId, spaceName: arrivalLocation.label });
            }
        }
    }

    private handleVisitorValidate(isValid: boolean): void
    {
        this.setState({ isValid: isValid });
    }

    public render(): JSX.Element
    {
        return (
            <IbssDialog
                open={this.props.showCreateVisitorDialog}
                onClose={() => this.props.closeClicked()}
                dialogContent=
                {
                    <>
                        {this.state.creatingVisitor && <LoadingOverlay />}
                        <div style={{ fontSize: '14px' }} className='mb-3'>{this.labels.HubLabelAddAdhocVisitorText}</div>
                        <Visitor
                            sx={{ padding: 0 }}
                            isFlex={this.state.isFlex}
                            buildingId={this.state.buildingId}
                            firstName={this.state.firstName}
                            lastName={this.state.lastName}
                            company={this.state.company}
                            email={this.state.email}
                            visitorTypeId={this.state.visitorTypeId}
                            onLoading={() => this.setState({ isLoading: true })}
                            onLoaded={() => this.setState({ isLoading: false })}
                            pacs={this.state.pacs}
                            notes={this.state.notes}
                            hostEmail={this.state.hostEmail}
                            visitorStartDate={this.state.arrival}
                            visitorEndDate={this.state.departure}
                            arrivalLocationId={this.state.visitorArrivalLocation}
                            arrivalLocations={this.state.arrivalSpaces}
                            onChange={props => this.handleVisitorChange(props)}
                            onValidate={isValid => this.handleVisitorValidate(isValid)}
                            buildingOptions={this.state.buildingOption}
                            buildingChanged={(buildingId) => this.buildingChanged(buildingId)}
                        />
                    </>
                }
                header={this.labels.HubLabelAddVisitor}
                footer=
                {
                    <>
                        <IbssButton disabled={!this.state.isValid || this.state.creatingVisitor} onClick={() => this.createVisitorRecord()} variant="contained" sx={{ width: '100px' }}>
                            {this.labels.HubLabelOk}
                        </IbssButton>
                    </>
                }
                fullWidth
            />
        )
    }
}

export default CreateVisitorDialog;

export interface IProps
{
    closeClicked: (submitted?: boolean) => void;
    showCreateVisitorDialog: boolean;
}

export interface IState
{
    isValid: boolean;
    isLoading: boolean,
    firstName: string;
    lastName: string;
    company: string;
    email: string;
    visitorTypeId: string;
    invalidVisitorEmail: boolean;
    hostEmail: string;
    host: IUser | null;
    pacs: string;
    arrival: DateTime;
    departure: DateTime;
    buildingId: number;
    creatingVisitor: boolean;
    arrivalError: string;
    departureError: string;
    spaceName: string;
    spaceId: string;
    notes: INote[];
    arrivalSpaces: ISelectOption<string, string>[];
    visitorArrivalLocation: string;
    isFlex: boolean,
    buildingOption: IBuildingOption[],
}

export interface IFlexInterface extends Omit<IState, "isFlex" | "buildingOption">
{

}

interface IBuildingOption
{
    label: string,
    value: number,
}
